/**
 * Service-worker registration, shared by every Joyia PWA.
 *
 * ## Why this is in elements rather than per app
 *
 * Every product that ships a worker needs exactly this: register once at boot, watch for a waiting
 * version, offer a reload. Copied per app it drifts — and the drift is invisible, because an app
 * with a stale worker looks identical to one with a current worker until somebody reports a fix
 * that never arrived.
 *
 * ## Why it is separate from `PushPrompt`
 *
 * Registration used to live inside `PushPrompt.getRegistration`, which made the whole service
 * worker conditional on three things that have nothing to do with offline support: the component
 * being mounted (it renders on `/inbox` and `/settings` only), `PushManager` being available, and
 * `VITE_VAPID_PUBLIC_KEY` being set — the component returns `unsupported` and never registers
 * without it.
 *
 * The practical effect was that a person who installed the app to their home screen and opened it
 * on `/overview` had no worker at all, so there was nothing to fall back to with no connection:
 * a launch offline showed the browser's error page. On iOS, where an installed PWA is the only way
 * to get push at all, that also meant no registration until they happened to visit the inbox.
 *
 * Registering at boot fixes both, and is the ordinary place for it. `PushPrompt` still calls
 * `register` itself — it needs the registration object to subscribe, and `register` on an
 * already-registered scope resolves with the existing one rather than installing twice.
 *
 * ## Why registration is deferred to `load`
 *
 * The worker's install fetches the shell, which competes with the page's own first load for
 * bandwidth on a phone. Waiting for `load` costs nothing (there is no worker to use on a first
 * visit anyway) and keeps the install off the critical path.
 */

/** Notified when a new worker is installed and waiting to take over. */
export type UpdateListener = (activate: () => void) => void;

let updateListener: UpdateListener | undefined;
let pendingUpdate: (() => void) | undefined;

/**
 * Subscribe to "a new version is ready".
 *
 * Fires immediately if an update was already found before the listener attached — the worker
 * installs in parallel with React mounting, so the race is normal rather than exceptional, and a
 * missed event would leave a stale tab with no way to learn about the update.
 */
export function onServiceWorkerUpdate(listener: UpdateListener): () => void {
  updateListener = listener;
  if (pendingUpdate) listener(pendingUpdate);
  return () => {
    updateListener = undefined;
  };
}

function announce(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting;
  if (!waiting) return;

  const activate = () => {
    // The worker calls `skipWaiting()` on install, so it is usually already active by the time a
    // user sees the prompt. `postMessage` covers the case where it is not, and the reload is what
    // actually swaps the page onto it either way.
    waiting.postMessage({ type: "SKIP_WAITING" });
    window.location.reload();
  };

  pendingUpdate = activate;
  updateListener?.(activate);
}

/**
 * Register `/sw.js` and wire the update signal.
 *
 * Call once, at module scope in the app entry — not from a component. Gating a worker on a route's
 * component being mounted is the bug this replaces: me's registration lived inside `PushPrompt`,
 * which renders on two routes only, so launching the installed app anywhere else left no worker and
 * therefore no offline shell.
 */
export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Never in a preview/mock harness: those replace `window.fetch` with canned responses, and a
  // worker intercepting navigations underneath that is one more moving part in a review tool.
  // Read off `import.meta.env` defensively — apps that have no preview mode simply never set it.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  if (env?.VITE_PREVIEW === "1") return;

  const start = () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((registration) => {
        // Already waiting when we registered — a previous visit installed it.
        if (registration.waiting && navigator.serviceWorker.controller) {
          announce(registration);
        }

        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            // `controller` is null on the very first install, and that is NOT an update: there is
            // no previous version to replace and nothing for the user to decide. Prompting then
            // would ask somebody to reload the page they just opened.
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              announce(registration);
            }
          });
        });
      })
      .catch(() => {
        // A failed registration is not worth surfacing: the app works without a worker, it simply
        // has no offline shell. Throwing here would be a visible error for a missing nicety.
      });
  };

  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}
